import { Button, Card, Grid, Text } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import useFacebookSDK from '../../helpers/useFacebookSDK';
import useFetch from '../../helpers/useFetch';
import { setWhatsAppAccount } from '../../store/whatsappSlice';
import ErrorMessage from '../ErrorMessage';

const WhatsAppConnectButton = () => {
  const dispatch = useDispatch();
  const fetch = useFetch();
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const { accountId, domainId } = useParams();
  const { sdkLoaded, sdkError } = useFacebookSDK();

  const assetIdsRef = useRef(null);

  useEffect(() => {
    const handleMessage = (event) => {
      const allowedOrigins = ['https://www.facebook.com', 'https://web.facebook.com'];
      if (!allowedOrigins.includes(event.origin)) return;
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (data.event === 'FINISH') {
            assetIdsRef.current = {
              wabaId: data.data.waba_id,
              phoneNumberId: data.data.phone_number_id,
              businessId: data.data.business_id,
            };
          } else if (data.event === 'CANCEL') {
            assetIdsRef.current = null;
          } else if (data.event === 'ERROR') {
            const { error_message } = data.data;
            console.error('error ', error_message);
          }
        }
      } catch {
        console.log('Non JSON Responses', event.data);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const fbLoginCallback = (response) => {
    if (response.authResponse) {
      const code = response.authResponse.code;

      (async () => {
        try {
          setError(null);

          if (!assetIdsRef.current?.wabaId) {
            throw new Error('WhatsApp Business Account ID is required');
          }

          const { whatsappAccount } = await fetch(
            `/api/admin/accounts/${accountId}/domains/${domainId}/whatsapp/embedded-signup`,
            {
              method: 'POST',
              body: JSON.stringify({
                code,
                wabaId: assetIdsRef.current.wabaId,
                phoneNumberId: assetIdsRef.current.phoneNumberId,
                businessId: assetIdsRef.current?.businessId,
              }),
            }
          );

          dispatch(setWhatsAppAccount(whatsappAccount));
        } catch (err) {
          console.error('WhatsApp Embedded Signup error:', err);
          setError(err.message || 'Failed to connect WhatsApp. Please try again.');
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setError('WhatsApp connection was cancelled or failed');
      setLoading(false);
    }
  };

  const launchWhatsAppSignup = () => {
    if (!sdkLoaded || !window.FB) {
      setError('Facebook SDK not loaded. Please refresh the page and try again.');
      return;
    }

    const configId = import.meta.env.VITE_WHATSAPP_EMBEDDED_SIGNUP_CONFIGURATION_ID;
    if (!configId) {
      setError('WhatsApp configuration ID is missing. Please check your environment variables.');
      return;
    }

    setError(null);
    setLoading(true);

    window.FB.login(fbLoginCallback, {
      config_id: configId,
      response_type: 'code',
      override_default_response_type: true,
      extras: { version: 'v3' },
    });
  };

  return (
    <Card withBorder>
      <Grid>
        <Grid.Col span={4}>
          <Text fw={500}>WhatsApp Connection</Text>
        </Grid.Col>
        <Grid.Col span={8}>
          {(error || sdkError) && <ErrorMessage message={error || sdkError} />}
          <Button
            color="green"
            onClick={launchWhatsAppSignup}
            loading={loading}
            disabled={!sdkLoaded}
            mt={error ? 'md' : 0}
          >
            {loading ? 'Connecting...' : 'Connect WhatsApp Business'}
          </Button>
        </Grid.Col>
      </Grid>
    </Card>
  );
};

export default WhatsAppConnectButton;
